import {LitElement, html, css, type CSSResultGroup} from 'lit';
import {SignalWatcher} from '@lit-labs/preact-signals';
import {morphedCreate, morphedUpdate, morphedDelete, morphedRead} from '../../services/morphed-entity.service.js';
import {loadEntityDefinitions, type EntityDefinition} from '../../services/entity-definition.service.js';
import {rolesSignal, loadPermissions, myRoles, type ClamRole, type RoleTool} from '../../services/permission.service.js';

const HARDCODED_TOOLS: {key: string; label: string}[] = [
    {key: 'people-loader', label: 'Importar Asistentes'},
    {key: 'gafete-loader', label: 'Asignar Gafetes'},
    {key: 'qr-manager', label: 'Invitaciones y Correos'},
    {key: 'email-template-editor', label: 'Plantillas de Correo'},
    {key: 'scanner', label: 'Escanear Asistencia'},
    {key: 'role-manager', label: 'Roles'},
    {key: 'user-manager', label: 'Usuarios'},
];

interface Toast {kind: 'ok' | 'err' | 'info'; text: string}

export class ClamRoleManager extends SignalWatcher(LitElement) {
    static styles: CSSResultGroup = css`
        :host { display: block; font-family: var(--clam-font); color: var(--clam-text); }

        .toolbar { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; padding: var(--clam-space-4, 16px); gap: var(--clam-space-3, 12px); }
        .toolbar__left { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .toolbar__title { font-size: var(--clam-text-xl, 20px); font-weight: 700; color: var(--clam-darkgray, #424244); margin: 0; }
        .toolbar__subtitle { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); margin: 0; }
        .toolbar__right { display: flex; align-items: center; gap: var(--clam-space-2, 8px); flex-wrap: wrap; }

        .btn { padding: 7px var(--clam-space-4, 16px); border-radius: var(--clam-radius-sm, 6px); border: none; cursor: pointer; font-size: var(--clam-text-sm, 13px); font-weight: 500; font-family: var(--clam-font); transition: background 0.15s; white-space: nowrap; }
        .btn-primary { background: var(--clam-light-green, #6bbb24); color: var(--clam-white, #fff); }
        .btn-primary:hover { background: var(--clam-green, #5a9d1e); }
        .btn-danger { background: var(--clam-danger, #ea4335); color: var(--clam-white, #fff); }
        .btn-danger:hover { background: #c83227; }
        .btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-sm { padding: 6px 12px; font-size: var(--clam-text-xs, 11px); min-height: 34px; }
        .btn-secondary { background: var(--clam-surface, #fff); color: var(--clam-text, #333); border: 1px solid var(--clam-border, #e2e8f0); }
        .btn-secondary:hover { background: var(--clam-surface-soft, #f5f6f8); }

        .cards-grid { display: grid; grid-template-columns: 1fr; gap: var(--clam-space-3, 12px); padding: 0 var(--clam-space-4, 16px) var(--clam-space-5, 20px); align-items: start; }
        .card { border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-lg, 12px); background: var(--clam-surface, #fff); box-shadow: var(--clam-shadow-sm, 0 1px 3px rgba(0,0,0,0.05)); display: flex; flex-direction: column; overflow: hidden; }
        .card__head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--clam-space-2, 8px); padding: var(--clam-space-3, 12px) var(--clam-space-4, 16px); }
        .card__title { font-size: var(--clam-text-base, 14px); font-weight: 700; color: var(--clam-text, #333); margin: 0; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .card__key { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); margin: 2px 0 0; }
        .card__body { padding: 0 var(--clam-space-4, 16px) var(--clam-space-3, 12px); display: flex; flex-direction: column; gap: var(--clam-space-2, 8px); }
        .card__desc { font-size: var(--clam-text-sm, 13px); color: var(--clam-text-muted, #6d6e71); line-height: 1.4; }
        .chips { display: flex; flex-wrap: wrap; gap: 4px; }
        .chip { background: var(--clam-surface-soft, #f0f2f5); color: var(--clam-text-muted, #6d6e71); border-radius: 999px; padding: 2px 9px; font-size: var(--clam-text-xs, 11px); }
        .chip--perm { background: #e8f5e0; color: #3f7a12; }
        .card__foot { display: flex; gap: var(--clam-space-2, 8px); padding: var(--clam-space-2, 8px) var(--clam-space-4, 16px) var(--clam-space-3, 12px); border-top: 1px solid var(--clam-border, #e2e8f0); flex-wrap: wrap; }
        .badge { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; padding: 2px 7px; border-radius: 999px; }
        .badge--sys { background: #fef3c7; color: #92400e; }
        .badge--me { background: #dcfce7; color: #166534; }

        .empty { text-align: center; padding: var(--clam-space-8, 40px); color: var(--clam-light-gray, #898a8c); font-size: var(--clam-text-base, 14px); }
        .spinner { display: inline-block; width: 15px; height: 15px; border: 2px solid rgba(255,255,255,0.4); border-top-color: currentColor; border-radius: 50%; animation: crrm-spin 0.7s linear infinite; vertical-align: middle; }
        @keyframes crrm-spin { to { transform: rotate(360deg); } }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: flex-end; justify-content: center; z-index: 200; padding: 0; }
        .modal { background: var(--clam-surface, #fff); border-radius: var(--clam-radius-lg, 12px) var(--clam-radius-lg, 12px) 0 0; width: 100%; max-width: 720px; max-height: calc(100dvh - 24px); display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 -8px 40px rgba(0,0,0,0.2); }
        .modal form { display: flex; flex-direction: column; min-height: 0; flex: 1; }
        .modal__header { flex-shrink: 0; display: flex; align-items: center; justify-content: space-between; gap: var(--clam-space-3, 12px); padding: var(--clam-space-4, 16px) var(--clam-space-5, 20px); border-bottom: 1px solid var(--clam-border, #e2e8f0); }
        .modal__header h2 { margin: 0; color: var(--clam-darkgray, #424244); font-size: var(--clam-text-lg, 18px); }
        .modal__close { flex-shrink: 0; width: 32px; height: 32px; border-radius: 50%; border: none; cursor: pointer; background: transparent; color: var(--clam-text-muted, #6d6e71); font-size: 18px; line-height: 1; display: inline-flex; align-items: center; justify-content: center; }
        .modal__close:hover { background: var(--clam-surface-soft, #f0f2f5); }
        .modal__body { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: var(--clam-space-4, 16px); }
        .modal__footer { flex-shrink: 0; padding: var(--clam-space-3, 12px) var(--clam-space-4, 16px); border-top: 1px solid var(--clam-border, #e2e8f0); background: var(--clam-surface, #fff); position: sticky; bottom: 0; }
        .modal-actions { display: flex; gap: var(--clam-space-3, 12px); justify-content: flex-end; }

        .form-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: var(--clam-space-4, 16px); }
        .form-group { display: flex; flex-direction: column; }
        .form-group label { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); margin-bottom: var(--clam-space-1, 4px); font-weight: 500; }
        .form-group input, .form-group textarea { width: 100%; padding: 8px 10px; border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px); font-size: var(--clam-text-sm, 13px); font-family: var(--clam-font); box-sizing: border-box; }
        .form-group input:focus, .form-group textarea:focus { outline: none; border-color: var(--clam-light-green, #6bbb24); }
        .form-section-title { font-size: var(--clam-text-sm, 13px); font-weight: 700; color: var(--clam-darkgray, #424244); margin: var(--clam-space-2, 8px) 0 var(--clam-space-1, 4px); padding-bottom: 6px; border-bottom: 1px solid var(--clam-border, #e2e8f0); }
        .toolrow { display: flex; align-items: center; gap: var(--clam-space-2, 8px); padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
        .toolrow:last-child { border-bottom: none; }
        .toolrow__name { flex: 1; min-width: 0; font-size: var(--clam-text-sm, 13px); color: var(--clam-text, #333); }
        .toolrow__name small { display: block; font-size: 10px; color: var(--clam-text-muted, #9ca3af); }
        .toolrow__flags { display: none; gap: var(--clam-space-2, 8px); flex-wrap: wrap; }
        .toolrow__flags.open { display: flex; }
        .flag { display: inline-flex; align-items: center; gap: 3px; font-size: 10px; color: var(--clam-text-muted, #6d6e71); cursor: pointer; user-select: none; }
        .check { width: 16px; height: 16px; cursor: pointer; accent-color: var(--clam-primary, #5a9d1e); }
        .toolrows { border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px); padding: 0 10px; margin-bottom: 8px; }
        .chip small { font-weight: 700; opacity: 0.7; margin-left: 3px; }
        .muted { font-size: var(--clam-text-xs, 11px); color: var(--clam-text-muted, #6d6e71); line-height: 1.4; }
        code { font-size: 10px; background: var(--clam-surface-soft, #f0f2f5); padding: 1px 5px; border-radius: 4px; word-break: break-all; }

        .toast { position: fixed; left: 50%; bottom: 24px; transform: translateX(-50%); z-index: 300; padding: 12px 20px; border-radius: var(--clam-radius-md, 8px); font-size: var(--clam-text-sm, 13px); font-weight: 600; box-shadow: 0 6px 20px rgba(0,0,0,0.18); max-width: 90vw; }
        .toast--ok { background: #dcfce7; color: #3f7a12; }
        .toast--err { background: #fee2e2; color: #b91c1c; }

        .ac { display: flex; flex-direction: column; gap: 6px; position: relative; }
        .ac__chips { display: flex; flex-wrap: wrap; gap: 6px; min-height: 4px; }
        .ac__chip { display: inline-flex; align-items: center; gap: 6px; background: #e8f5e0; color: #3f7a12; border-radius: 999px; padding: 4px 6px 4px 11px; font-size: var(--clam-text-xs, 11px); }
        .ac__chip small { color: #5a8a3a; }
        .ac__chip-x { border: none; background: rgba(63,122,18,0.2); color: #3f7a12; width: 18px; height: 18px; border-radius: 50%; cursor: pointer; font-size: 13px; line-height: 1; display: inline-flex; align-items: center; justify-content: center; }
        .ac__chip-x:hover { background: rgba(63,122,18,0.35); }
        .ac__input { width: 100%; padding: 8px 10px; border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px); font-size: var(--clam-text-sm, 13px); font-family: var(--clam-font); box-sizing: border-box; }
        .ac__input:focus { outline: none; border-color: var(--clam-light-green, #6bbb24); }
        .ac__list { position: absolute; top: 100%; left: 0; right: 0; z-index: 20; background: var(--clam-surface, #fff); border: 1px solid var(--clam-border, #e2e8f0); border-radius: var(--clam-radius-sm, 6px); box-shadow: var(--clam-shadow-md, 0 4px 16px rgba(0,0,0,0.12)); max-height: 240px; overflow-y: auto; }
        .ac__item { padding: 9px 12px; cursor: pointer; display: flex; flex-direction: column; gap: 1px; border-bottom: 1px solid #f3f4f6; }
        .ac__item:last-child { border-bottom: none; }
        .ac__item:hover { background: rgba(90,157,30,0.08); }
        .ac__item small { color: var(--clam-text-muted, #6d6e71); font-size: 10px; }
        .ac__empty { padding: 10px 12px; color: var(--clam-text-muted, #6d6e71); font-size: var(--clam-text-xs, 11px); }

        @media (min-width: 640px) {
            .toolbar { padding: var(--clam-space-5, 20px) var(--clam-space-6, 24px); }
            .cards-grid { grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); padding: 0 var(--clam-space-6, 24px) var(--clam-space-6, 24px); gap: var(--clam-space-4, 16px); }
            .modal-overlay { align-items: center; padding: var(--clam-space-4, 16px); }
            .modal { border-radius: var(--clam-radius-lg, 12px); }
            .modal__body { padding: var(--clam-space-5, 20px); }
            .modal__footer { padding: var(--clam-space-3, 12px) var(--clam-space-5, 20px); }
            .form-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
        }
    `;

    private _toolsCatalog: {key: string; label: string}[] = [];
    private _editing: ClamRole | null = null;
    private _isNew = false;
    private _deleteTarget: ClamRole | null = null;
    private _toast: Toast | null = null;
    private _saving = false;
    private _toolQuery = '';
    private _toolsOpen = false;
    private _morphedUsers: {id: string; name?: string; family_name?: string; email?: string}[] = [];
    private _userQuery = '';
    private _usersOpen = false;

    async connectedCallback(): Promise<void> {
        super.connectedCallback();
        try {
            const [defs, users] = await Promise.all([
                loadEntityDefinitions(),
                morphedRead<{id: string; name?: string; family_name?: string; email?: string}>('morphed-user', {filters: [], options: {limit: 200}}).catch(() => []),
            ]);
            this._toolsCatalog = [
                ...defs.map((d: EntityDefinition) => ({key: d.entityEndpoint, label: d.entityName.replace('Clam ', '')})),
                ...HARDCODED_TOOLS,
            ];
            this._morphedUsers = users;
        } catch { this._toolsCatalog = [...HARDCODED_TOOLS]; }
        this.requestUpdate();
    }

    private _toolLabel(key: string): string {
        return this._toolsCatalog.find((t) => t.key === key)?.label || key;
    }
    private _addTool(key: string): void {
        if (!this._editing) return;
        const tools = [...(this._editing.tools || [])];
        if (!tools.find((t) => t.toolKey === key)) tools.push({toolKey: key, canView: true, canCreate: false, canEdit: false, canDelete: false});
        this._editing = {...this._editing, tools};
        this._toolQuery = '';
        this.requestUpdate();
    }
    /** Normaliza un tool del rol (los legacy cargados de la API solo traen {toolKey, canView}). */
    private _normTool(t: Partial<RoleTool>): RoleTool {
        return {toolKey: t.toolKey || '', canView: !!t.canView, canCreate: !!t.canCreate, canEdit: !!t.canEdit, canDelete: !!t.canDelete};
    }
    private _toggleToolFlag(toolKey: string, flag: 'canView' | 'canCreate' | 'canEdit' | 'canDelete', value: boolean): void {
        if (!this._editing) return;
        const tools = (this._editing.tools || []).map((t) => {
            if (t.toolKey !== toolKey) return t;
            return {...this._normTool(t), [flag]: value};
        });
        this._editing = {...this._editing, tools};
        this.requestUpdate();
    }
    private _renderToolRow(t: RoleTool): unknown {
        return html`
            <div class="toolrow">
                <div class="toolrow__name">${this._toolLabel(t.toolKey)}<small>${t.toolKey}</small></div>
                <div class="toolrow__flags open">
                    <label class="flag" title="Ver"><input type="checkbox" class="check" .checked=${t.canView} @change=${(e: Event) => this._toggleToolFlag(t.toolKey, 'canView', (e.target as HTMLInputElement).checked)} />V</label>
                    <label class="flag" title="Crear"><input type="checkbox" class="check" .checked=${t.canCreate} @change=${(e: Event) => this._toggleToolFlag(t.toolKey, 'canCreate', (e.target as HTMLInputElement).checked)} />C</label>
                    <label class="flag" title="Editar"><input type="checkbox" class="check" .checked=${t.canEdit} @change=${(e: Event) => this._toggleToolFlag(t.toolKey, 'canEdit', (e.target as HTMLInputElement).checked)} />E</label>
                    <label class="flag" title="Eliminar"><input type="checkbox" class="check" .checked=${t.canDelete} @change=${(e: Event) => this._toggleToolFlag(t.toolKey, 'canDelete', (e.target as HTMLInputElement).checked)} />D</label>
                </div>
                <button type="button" class="ac__chip-x" title="Quitar herramienta" @click=${() => this._removeTool(t.toolKey)}>×</button>
            </div>`;
    }
    private _removeTool(key: string): void {
        if (!this._editing) return;
        this._editing = {...this._editing, tools: (this._editing.tools || []).filter((t) => t.toolKey !== key)};
        this.requestUpdate();
    }
    private get _filteredTools(): {key: string; label: string}[] {
        const q = this._toolQuery.trim().toLowerCase();
        const selected = new Set((this._editing?.tools || []).map((t) => t.toolKey));
        return this._toolsCatalog
            .filter((t) => !selected.has(t.key))
            .filter((t) => !q || t.label.toLowerCase().includes(q) || t.key.toLowerCase().includes(q))
            .slice(0, 10);
    }

    private _userLabel(id: string): string {
        const u = this._morphedUsers.find((x) => x.id === id);
        return u ? (u.name || u.email || id) : id;
    }
    private _userSub(id: string): string {
        return this._morphedUsers.find((x) => x.id === id)?.email || '';
    }
    private _addUser(id: string): void {
        if (!this._editing) return;
        const users = [...(this._editing.users || [])];
        if (!users.includes(id)) users.push(id);
        this._editing = {...this._editing, users};
        this._userQuery = '';
        this.requestUpdate();
    }
    private _removeUser(id: string): void {
        if (!this._editing) return;
        this._editing = {...this._editing, users: (this._editing.users || []).filter((u) => u !== id)};
        this.requestUpdate();
    }
    private get _filteredUsers(): {id: string; name?: string; email?: string}[] {
        const q = this._userQuery.trim().toLowerCase();
        const selected = new Set(this._editing?.users || []);
        return this._morphedUsers
            .filter((u) => !selected.has(u.id))
            .filter((u) => !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
            .slice(0, 10);
    }

    private _toast_(kind: Toast['kind'], text: string): void {
        this._toast = {kind, text};
        this.requestUpdate();
        setTimeout(() => { this._toast = null; this.requestUpdate(); }, 3200);
    }

    private _newRole(): void { this._editing = {id: '', key: '', name: '', description: '', tools: [], users: [], isSystem: false}; this._isNew = true; this.requestUpdate(); }
    private _editRole(r: ClamRole): void {
        const clone = JSON.parse(JSON.stringify(r)) as ClamRole;
        clone.tools = (clone.tools || []).map((t) => this._normTool(t));
        this._editing = clone; this._isNew = false; this.requestUpdate();
    }
    private _cancelEdit(): void { this._editing = null; this._isNew = false; this.requestUpdate(); }
    private _closeDelete(): void { this._deleteTarget = null; this.requestUpdate(); }
    private _patch(p: Partial<ClamRole>): void { if (!this._editing) return; this._editing = {...this._editing, ...p}; this.requestUpdate(); }

    private async _save(e: Event): Promise<void> {
        e.preventDefault();
        if (!this._editing) return;
        const r = this._editing;
        if (!r.key?.trim() || !r.name?.trim()) { this._toast_('err', 'Key y Nombre son obligatorios.'); return; }
        const payload: Record<string, unknown> = {
            key: r.key.trim(), name: r.name.trim(), description: r.description || '',
            // Persistimos todos los tools asignados con sus flags CRUD completos
            // (sin filtrar por canView: un tool puede estar asignado con canView=false
            // para el caso "asignado pero sin permiso de lectura").
            tools: (r.tools || []).map((t) => this._normTool(t)),
            users: r.users || [], isSystem: !!r.isSystem,
        };
        this._saving = true; this.requestUpdate();
        try {
            if (this._isNew) { await morphedCreate('clam-rol', [payload]); this._toast_('ok', `Rol "${payload.name}" creado.`); }
            else { await morphedUpdate('clam-rol', r.id, payload); this._toast_('ok', `Rol "${payload.name}" actualizado.`); }
            this._editing = null; this._isNew = false;
            await loadPermissions(true);
        } catch (err) { this._toast_('err', `Error: ${String(err)}`); }
        finally { this._saving = false; this.requestUpdate(); }
    }

    private _askDelete(r: ClamRole): void {
        if (r.isSystem) { this._toast_('err', 'Los roles de sistema no se pueden eliminar.'); return; }
        this._deleteTarget = r; this.requestUpdate();
    }
    private async _confirmDelete(): Promise<void> {
        if (!this._deleteTarget) return;
        const r = this._deleteTarget;
        try { await morphedDelete('clam-rol', [r.id]); this._toast_('ok', `Rol "${r.name}" eliminado.`); await loadPermissions(true); }
        catch (err) { this._toast_('err', `Error: ${String(err)}`); }
        this._deleteTarget = null; this.requestUpdate();
    }

    render() {
        const roles = rolesSignal.value;
        const mine = new Set(myRoles.value.map((r) => r.id));
        return html`
            <div class="toolbar">
                <div class="toolbar__left">
                    <h1 class="toolbar__title">Roles</h1>
                    <p class="toolbar__subtitle">Controla el acceso a las herramientas · ${roles.length} rol(es)</p>
                </div>
                <div class="toolbar__right">
                    <button class="btn btn-primary" @click=${() => this._newRole()}>+ Nuevo Rol</button>
                </div>
            </div>

            ${roles.length === 0
                ? html`<div class="empty">Sin roles definidos. Crea el rol <b>admin</b> y asígnate tu sub para habilitar la gestión.</div>`
                : html`<div class="cards-grid">${roles.map((r) => html`
                    <div class="card">
                        <div class="card__head">
                            <div>
                                <h3 class="card__title">${r.name} ${r.isSystem ? html`<span class="badge badge--sys">sistema</span>` : ''} ${mine.has(r.id) ? html`<span class="badge badge--me">tú</span>` : ''}</h3>
                                <p class="card__key">key: <code>${r.key}</code> · ${(r.users || []).length} usuario(s)</p>
                            </div>
                        </div>
                        <div class="card__body">
                            ${r.description ? html`<p class="card__desc">${r.description}</p>` : ''}
                            <div class="chips">${(r.tools || []).map((t) => {
                                const nt = this._normTool(t);
                                const flags = (nt.canView ? 'V' : '') + (nt.canCreate ? 'C' : '') + (nt.canEdit ? 'E' : '') + (nt.canDelete ? 'D' : '');
                                return html`<span class="chip chip--perm" title=${nt.toolKey}>${this._toolLabel(nt.toolKey)}${flags ? html`<small>${flags}</small>` : ''}</span>`;
                            })}</div>
                        </div>
                        <div class="card__foot">
                            <button class="btn btn-secondary btn-sm" @click=${() => this._editRole(r)}>Editar</button>
                            <button class="btn btn-danger btn-sm" ?disabled=${!!r.isSystem} @click=${() => this._askDelete(r)}>Eliminar</button>
                        </div>
                    </div>`)}
                </div>`}

            ${this._editing ? this._renderForm() : ''}
            ${this._deleteTarget ? this._renderDelete() : ''}
            ${this._toast ? html`<div class="toast toast--${this._toast.kind}">${this._toast.text}</div>` : ''}
        `;
    }

    private _renderForm() {
        const r = this._editing!;
        return html`
            <div class="modal-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this._cancelEdit(); }}>
                <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
                    <form @submit=${(e: SubmitEvent) => this._save(e)}>
                        <div class="modal__header">
                            <h2>${this._isNew ? 'Nuevo' : 'Editar'} rol</h2>
                            <button type="button" class="modal__close" title="Cerrar" @click=${() => this._cancelEdit()}>×</button>
                        </div>
                        <div class="modal__body">
                            <div class="form-grid">
                                <div class="form-group">
                                    <label>Key técnica (única)</label>
                                    <input type="text" .value=${r.key} ?disabled=${!this._isNew} placeholder="admin / editor-noticias" @input=${(e: Event) => this._patch({key: (e.target as HTMLInputElement).value})}>
                                </div>
                                <div class="form-group">
                                    <label>Nombre</label>
                                    <input type="text" .value=${r.name} @input=${(e: Event) => this._patch({name: (e.target as HTMLInputElement).value})}>
                                </div>
                                <div class="form-group" style="grid-column:1/-1">
                                    <label>Descripción</label>
                                    <textarea rows="2" .value=${r.description || ''} @input=${(e: Event) => this._patch({description: (e.target as HTMLTextAreaElement).value})}></textarea>
                                </div>
                            </div>

                            <p class="form-section-title">Herramientas (tools)</p>
                            <span class="muted" style="display:block;margin-bottom:8px">Asigna herramientas y configura los permisos CRUD por tool. <b>V</b>=Ver · <b>C</b>=Crear · <b>E</b>=Editar · <b>D</b>=Eliminar.</span>
                            ${(r.tools || []).length > 0 ? html`
                                <div class="toolrows">
                                    ${(r.tools || []).map((t) => this._renderToolRow(this._normTool(t)))}
                                </div>
                            ` : html`<div class="ac__empty" style="padding:8px 0">Sin herramientas asignadas. Usa el buscador para agregar.</div>`}
                            <div class="ac">
                                <input class="ac__input" type="text" placeholder="Buscar herramienta para agregar..." .value=${this._toolQuery}
                                    @focus=${() => { this._toolsOpen = true; this.requestUpdate(); }}
                                    @blur=${() => setTimeout(() => { this._toolsOpen = false; this.requestUpdate(); }, 200)}
                                    @input=${(e: Event) => { this._toolQuery = (e.target as HTMLInputElement).value; this.requestUpdate(); }}>
                                ${(this._toolQuery.trim() || this._toolsOpen) ? html`<div class="ac__list">${this._filteredTools.length ? this._filteredTools.map((t) => html`<div class="ac__item" @mousedown=${(e: Event) => e.preventDefault()} @click=${() => this._addTool(t.key)}><b>${t.label}</b> <small>${t.key}</small></div>`) : html`<div class="ac__empty">Sin coincidencias</div>`}</div>` : ''}
                            </div>

                            <p class="form-section-title">Usuarios asignados</p>
                            <div class="ac">
                                <div class="ac__chips">
                                    ${(r.users || []).map((id) => html`<span class="ac__chip">${this._userLabel(id)}${this._userSub(id) ? html` <small>${this._userSub(id)}</small>` : ''}<button type="button" class="ac__chip-x" @click=${() => this._removeUser(id)}>×</button></span>`)}
                                </div>
                                <input class="ac__input" type="text" placeholder="Buscar usuario por nombre o email..." .value=${this._userQuery}
                                    @focus=${() => { this._usersOpen = true; this.requestUpdate(); }}
                                    @blur=${() => setTimeout(() => { this._usersOpen = false; this.requestUpdate(); }, 200)}
                                    @input=${(e: Event) => { this._userQuery = (e.target as HTMLInputElement).value; this.requestUpdate(); }}>
                                ${(this._userQuery.trim() || this._usersOpen) ? html`<div class="ac__list">${this._filteredUsers.length ? this._filteredUsers.map((u) => html`<div class="ac__item" @mousedown=${(e: Event) => e.preventDefault()} @click=${() => this._addUser(u.id)}><b>${u.name || u.email || u.id}</b> <small>${u.email || ''}</small></div>`) : html`<div class="ac__empty">Sin coincidencias</div>`}</div>` : ''}
                            </div>
                        </div>
                        <div class="modal__footer">
                            <div class="modal-actions">
                                <button type="button" class="btn btn-secondary" ?disabled=${this._saving} @click=${() => this._cancelEdit()}>Cancelar</button>
                                <button type="submit" class="btn btn-primary" ?disabled=${this._saving}>${this._saving ? html`<span class="spinner"></span> Guardando...` : (this._isNew ? 'Crear' : 'Guardar')}</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>`;
    }

    private _renderDelete() {
        const r = this._deleteTarget!;
        return html`
            <div class="modal-overlay" style="align-items:center" @click=${(e: Event) => { if (e.target === e.currentTarget) this._closeDelete(); }}>
                <div class="modal" style="max-width:420px" @click=${(e: Event) => e.stopPropagation()}>
                    <div class="modal__header"><h2>Eliminar rol</h2><button type="button" class="modal__close" @click=${() => this._closeDelete()}>×</button></div>
                    <div class="modal__body">
                        <p class="muted" style="font-size:14px;color:var(--clam-text,#333);line-height:1.5;margin:0">¿Seguro que deseas eliminar el rol <b>${r.name}</b>? Los usuarios asignados perderán los permisos asociados.</p>
                    </div>
                    <div class="modal__footer"><div class="modal-actions">
                        <button class="btn btn-secondary" @click=${() => this._closeDelete()}>Cancelar</button>
                        <button class="btn btn-danger" @click=${() => this._confirmDelete()}>Eliminar</button>
                    </div></div>
                </div>
            </div>`;
    }
}

customElements.define('clam-role-manager', ClamRoleManager);

declare global {
    interface HTMLElementTagNameMap {
        'clam-role-manager': ClamRoleManager;
    }
}
